import { createSelector } from 'reselect';
import { getStateAtNamespaceKey } from 'redux/get-state-at-namespace-key';
import {
  type ShopByListEligibilityState,
  type ShopByListEligibilitySuccessState,
  type ShopByListItemState,
  type ShopByListProcessingActiveState,
  ShopByListProcessingPhase,
  ShopByListProcessingState,
  ShopByListSuggestionsStatus,
} from 'redux/modules/shop-by-list/types';
import type { LineNumber } from 'constants/products';
import { STATUS_ELIGIBLE } from 'constants/quick-shop';
import type { RecommendedSelection } from 'redux/transforms/types';
import { formatAsPositivePoundsOrPence } from 'utils/currency';

const isEligibilitySuccessfullyLoaded = (
  eligibility: ShopByListEligibilityState,
): eligibility is ShopByListEligibilitySuccessState => 'status' in eligibility;

const isProcessing = (
  processing: ShopByListProcessingState,
): processing is ShopByListProcessingActiveState => 'phase' in processing;

const getShopByList = (state: WtrState) => getStateAtNamespaceKey(state, 'shopByList');

const getEligibility = createSelector(getShopByList, shopByList => shopByList.eligibility);
const getProcessing = createSelector(getShopByList, shopByList => shopByList.processing);

export const getIsEligibilityLoaded = createSelector(getEligibility, eligibility =>
  isEligibilitySuccessfullyLoaded(eligibility),
);

export const getIsEligibilityLoading = createSelector(
  getEligibility,
  eligibility => eligibility.loading ?? true,
);

export const getIsEligible = createSelector(getEligibility, eligibility =>
  isEligibilitySuccessfullyLoaded(eligibility) ? eligibility.status === STATUS_ELIGIBLE : false,
);

export const getProcessingPhase = createSelector(getProcessing, processing =>
  isProcessing(processing) ? processing.phase : ShopByListProcessingPhase.IDLE,
);

export const getIsExtractingSearchTerms = createSelector(
  getProcessingPhase,
  processingPhase => processingPhase === ShopByListProcessingPhase.EXTRACTING_SEARCH_TERMS,
);

export const getIsNoResults = createSelector(
  getProcessingPhase,
  processingPhase => processingPhase === ShopByListProcessingPhase.NO_RESULTS,
);

export const getIsExtractingAdditionalSearchTerms = createSelector(
  getProcessingPhase,
  processingPhase =>
    processingPhase === ShopByListProcessingPhase.EXTRACTING_ADDITIONAL_SEARCH_TERMS,
);

export const getIsStreamingSuggestions = createSelector(
  getProcessingPhase,
  processingPhase =>
    processingPhase === ShopByListProcessingPhase.STREAMING_SUGGESTIONS ||
    processingPhase === ShopByListProcessingPhase.EXTRACTING_ADDITIONAL_SEARCH_TERMS,
);

export const getIsStreamingSuggestionsCompleted = createSelector(
  getProcessing,
  getIsStreamingSuggestions,
  (processing, isStreamingSuggestions) => {
    if (!isStreamingSuggestions) {
      return false;
    }

    const { itemOrder, itemsById } = processing;

    return itemOrder.every(itemId => {
      const { suggestionsStatus } = itemsById[itemId];

      return suggestionsStatus === ShopByListSuggestionsStatus.SUCCESS;
    });
  },
);

const emptyListItems: ShopByListItemState[] = [];

export const getProcessedListItems = createSelector(getProcessing, processing => {
  if (!isProcessing(processing)) {
    return emptyListItems;
  }

  const { itemOrder, itemsById } = processing;

  return itemOrder.map(itemId => {
    const { candidates, originalSearchTerm, searchTerm, searchTermType, suggestionsStatus } =
      itemsById[itemId];

    return {
      candidates,
      itemId,
      originalSearchTerm,
      searchTerm,
      searchTermType,
      suggestionsStatus,
    };
  });
});

const EMPTY_RECOMMENDED_SELECTION: RecommendedSelection[] = [];

export const getRecommendedSelection = createSelector(
  getProcessing,
  processing => processing.recommendedSelection ?? EMPTY_RECOMMENDED_SELECTION,
);

const EMPTY_AVAILABLE_LINE_NUMBERS: Set<LineNumber> = new Set();

export const getAvailableLineNumbers = createSelector(getProcessedListItems, listItems => {
  const availableLineNumbers = new Set<LineNumber>();

  for (const { candidates } of listItems) {
    for (const { searchProduct } of candidates) {
      availableLineNumbers.add(searchProduct);
    }
  }

  if (availableLineNumbers.size === 0) {
    return EMPTY_AVAILABLE_LINE_NUMBERS;
  }

  return availableLineNumbers;
});

export const getEstimatedPriceLoading = createSelector(
  getProcessing,
  processing => processing.estimatedPriceLoading ?? true,
);

export const getEstimatedPrice = createSelector(
  [getProcessing, getEstimatedPriceLoading],
  (processing, loading) => {
    if (typeof processing.estimatedPrice !== 'undefined') {
      return formatAsPositivePoundsOrPence(processing.estimatedPrice);
    }

    if (loading) {
      return '';
    }

    return 'to be calculated at trolley';
  },
);

export const getViewAllModalProducts = createSelector(
  getShopByList,
  shopByList => shopByList.viewAllModalProducts.products,
);

export const getViewAllModalIsLoading = createSelector(
  getShopByList,
  shopByList => shopByList.viewAllModalProducts.isLoading,
);

export const getViewAllModalHasError = createSelector(
  getShopByList,
  shopByList => shopByList.viewAllModalProducts.hasError,
);
